import {
  getSavedTimeRange,
  shouldPopulateStoreFromSearchParams,
} from '@modules/dashboard/helpers';
import { DashboardActionType } from '@modules/dashboard/store/dashboardActionType';
import { DashboardQuery } from '@modules/dashboard/store/types';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import { FilterClauseViewFragment } from 'generated/graphql';
import { useEffect } from 'react';
import { useMount } from 'react-use';
import useGetSourceDetailsMap from 'v2/core/repository/edge-server/source-details';
import { mapToEQL } from 'v2/core/repository/eql/mappers';
import { parseEQL } from 'v2/core/repository/eql/parser';
import { ExpressionType } from 'v2/core/repository/eql/types';
import { EmptyWhereClause } from 'v2/core/services/queryBuilder/whereClause';
import { useGetUser } from 'v2/core/services/user';
import { useInitDashboardStoreFromSearchParams } from './useInitDashboardStoreFromSearchParams';

export const useInitDashboardQuery = (
  dashboardFilters?: FilterClauseViewFragment,
) => {
  const {
    tenant: { timezone },
  } = useGetUser();
  const { data: sourceDetailsMap } = useGetSourceDetailsMap();
  const dispatch = useDashboardStore(({ dispatch }) => dispatch);
  const initDashboardStoreFromSearchParams =
    useInitDashboardStoreFromSearchParams();

  useMount(() => {
    dispatch({
      type: DashboardActionType.INIT_QUERY,
      payload: {
        whereClause: EmptyWhereClause,
        duration: undefined,
      },
    });
  });

  useEffect(() => {
    if (shouldPopulateStoreFromSearchParams()) {
      initDashboardStoreFromSearchParams();
      return;
    }

    if (dashboardFilters && sourceDetailsMap) {
      const hasWhereClause = Boolean(dashboardFilters?.whereClause);
      const hasTimeRange = Boolean(dashboardFilters?.timeRange);
      const queryPayload: Partial<DashboardQuery> = {
        whereClause: EmptyWhereClause,
        duration: undefined,
      };

      if (hasWhereClause) {
        const eqlExpression = `${mapToEQL({
          expressionType: ExpressionType.Records,
          sourceDetailsMap,
        })} where ${dashboardFilters.whereClause}`;

        queryPayload.whereClause =
          parseEQL(eqlExpression, sourceDetailsMap, timezone).whereClause ??
          EmptyWhereClause;
      }

      if (hasTimeRange) {
        queryPayload.duration = getSavedTimeRange({
          timeRange: dashboardFilters.timeRange,
        });
      }

      dispatch({
        type: DashboardActionType.SET_QUERY,
        payload: {
          ...queryPayload,
        },
      });
    }
  }, [
    dashboardFilters,
    dispatch,
    initDashboardStoreFromSearchParams,
    sourceDetailsMap,
    timezone,
  ]);
};
